import { useState, useEffect, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import CoverageStrip from '../../components/feature/CoverageStrip';
import SiteWidgets from '../../components/feature/SiteWidgets';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

function BatteryQuoteForm() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [postcode, setPostcode] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!postcode.trim()) { setError('Please enter your postcode'); return; }
    setError('');
    setSubmitting(true);

    // Submit to form endpoint in background
    try {
      const body = new URLSearchParams({ service: 'Battery Storage', postcode });
      fetch('https://readdy.ai/api/form/d7426jr41deorl02el20', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
    } catch {
      // Silent fail - still redirect
    }

    // Redirect to solar quote wizard
    const params = new URLSearchParams({ service: 'Battery Storage', postcode });
    navigate(`/solar-quote?${params.toString()}`);
  };

  if (submitted) {
    return (
      <div className="text-center py-6">
        <div className="w-16 h-16 flex items-center justify-center mx-auto mb-3">
          <i className="ri-checkbox-circle-fill text-[#7AC943] text-6xl" />
        </div>
        <h3 className="font-bold text-xl text-[#1F2933] mb-2">Taking you to the quote wizard...</h3>
        <p className="text-gray-500 text-sm">Just a moment while we set up your battery quote.</p>
      </div>
    );
  }

  return (
    <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <input
        type="text"
        name="postcode"
        value={postcode}
        onChange={(e) => setPostcode(e.target.value.toUpperCase())}
        placeholder="Enter your postcode"
        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3.5 text-sm text-[#1F2933] focus:outline-none focus:border-[#4CAF50] transition-colors"
        maxLength={8}
      />
      {error && <p className="text-red-500 text-xs">{error}</p>}
      <button type="submit" disabled={submitting} className="w-full bg-gradient-to-r from-[#145A32] to-[#4CAF50] text-white font-bold py-4 rounded-xl text-sm hover:opacity-90 transition-all cursor-pointer whitespace-nowrap disabled:opacity-70">
        {submitting ? 'Getting your quote...' : 'Get my battery quote →'}
      </button>
      <p className="text-xs text-gray-400 text-center">No obligation. Fixed prices. Response within 24 hours.</p>
    </form>
  );
}

const benefits = [
  { icon: 'ri-moon-line', title: 'Power your home overnight', desc: 'Store excess daytime solar energy and use it after dark — stop sending it back to the grid.' },
  { icon: 'ri-flashlight-line', title: 'Cut grid dependency by 90%', desc: 'A correctly-sized battery paired with solar can cover the vast majority of your energy needs.' },
  { icon: 'ri-money-pound-circle-line', title: 'Save up to £700/year', desc: 'Maximise self-consumption and avoid peak-rate electricity — most batteries pay back within 7 years.' },
  { icon: 'ri-plug-line', title: 'Works with existing solar', desc: 'Add battery storage to a system you already have — no need to replace your existing panels.' },
  { icon: 'ri-shield-check-line', title: '10-year battery warranty', desc: 'All our batteries come with a full 10-year manufacturer warranty and 80% capacity guarantee.' },
  { icon: 'ri-home-wifi-line', title: 'Smart app control', desc: 'Monitor your energy production, usage and battery level in real time from your smartphone.' },
];

const models = [
  { brand: 'GivEnergy', capacity: '9.5kWh', cycles: '6,000+', warranty: '10 years', price: '£2,995', desc: 'Perfect entry-level battery — popular choice for 3-4 bedroom homes with existing solar.' },
  { brand: 'Tesla Powerwall 3', capacity: '13.5kWh', cycles: 'Unlimited', warranty: '10 years', price: '£4,495', desc: 'The most powerful home battery on the market. Fully integrated inverter and smart energy management.', popular: true },
  { brand: 'GivEnergy All-in-One', capacity: '15kWh', cycles: '6,000+', warranty: '10 years', price: '£5,995', desc: 'Inverter, battery and energy management all in one unit — perfect for new solar installations.' },
];

const faqs = [
  { q: 'Do I need solar panels to install a battery?', a: 'No — a battery can work without solar panels, charging from the grid at cheap off-peak tariffs like Octopus Agile. However, combining solar and battery gives the greatest savings.' },
  { q: 'How long will a battery power my home?', a: 'A 10kWh battery will power a typical 3-bed home for around 12–16 hours overnight. This varies based on your usage and appliances.' },
  { q: 'Can I add a second battery later?', a: 'Yes. Most of our battery systems are modular and expandable. We can add additional capacity whenever you need it.' },
  { q: 'Is there any government grant for batteries?', a: 'Currently there is no specific government grant, but batteries installed alongside solar panels qualify for 0% VAT on the full installation cost.' },
];

export default function BatteryPage() {
  const [visible, setVisible] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const benefitsAnim = useScrollAnimation();
  const modelsAnim = useScrollAnimation();
  const faqAnim = useScrollAnimation();

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <main className="min-h-screen bg-white font-sans">
      <Header />

      {/* Hero */}
      <section className="bg-[#145A32] pt-24 md:pt-40 pb-0 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            <div className={`flex-1 pb-8 md:pb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
              <Link to="/" className="inline-flex items-center gap-1 text-white/60 hover:text-white text-sm mb-6 transition-colors cursor-pointer">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line text-sm" /></div>
                Back to home
              </Link>
              <div className="text-[#7AC943] text-xs font-bold uppercase tracking-widest mb-3">BATTERY STORAGE</div>
              <h1 className="font-heading font-black text-white text-4xl md:text-5xl leading-tight mb-4">
                Home battery<br />storage systems
              </h1>
              <p className="text-white/70 text-base md:text-lg leading-relaxed mb-6 max-w-lg">
                Store your solar energy and use it when you need it most. Stop sending power back to the grid — keep it for yourself and slash your electricity bills.
              </p>
              <div className="flex flex-wrap gap-3">
                <a href="#get-quote" className="bg-[#7AC943] hover:bg-[#6ab538] text-white font-bold px-6 py-3.5 rounded-full text-sm transition-all cursor-pointer whitespace-nowrap">
                  Get a free quote
                </a>
                <a href="#models" className="bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3.5 rounded-full text-sm transition-all cursor-pointer whitespace-nowrap">
                  View batteries
                </a>
              </div>
              <div className="flex flex-wrap gap-6 mt-8">
                {['10-yr Warranty', 'Works with Existing Solar', 'Finance Available'].map(t => (
                  <div key={t} className="flex items-center gap-2 text-white/80 text-sm">
                    <div className="w-4 h-4 flex items-center justify-center"><i className="ri-check-line text-[#7AC943]" /></div>
                    {t}
                  </div>
                ))}
              </div>
            </div>
            <div className={`flex-1 flex items-end justify-center md:justify-end transition-all duration-1000 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
              <img
                src="https://public.readdy.ai/ai/img_res/2217c7df9c80d81561c6e645009fd9c3.jpg"
                alt="Home battery storage"
                className="w-full max-w-lg h-[300px] md:h-[420px] object-cover object-center rounded-t-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div
            ref={benefitsAnim.ref}
            className={`text-center mb-10 transition-all duration-700 ${benefitsAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <div className="text-xs font-bold uppercase tracking-widest text-[#4CAF50] mb-2">WHY ADD BATTERY?</div>
            <h2 className="font-heading font-black text-[#1F2933] text-2xl md:text-3xl">Why add battery storage?</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map((b, i) => (
              <div
                key={b.title}
                style={{ transitionDelay: `${i * 80}ms` }}
                className={`bg-[#f7f8fa] rounded-xl p-6 hover:-translate-y-1 transition-all duration-300 ${benefitsAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#145A32]/10 mb-4">
                  <i className={`${b.icon} text-[#145A32] text-xl`} />
                </div>
                <h3 className="font-bold text-[#1F2933] text-base mb-2">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Models */}
      <section id="models" className="py-14 md:py-20 bg-[#f7f8fa]">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div
            ref={modelsAnim.ref}
            className={`text-center mb-10 transition-all duration-700 ${modelsAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <div className="text-xs font-bold uppercase tracking-widest text-[#4CAF50] mb-2">PRODUCTS</div>
            <h2 className="font-heading font-black text-[#1F2933] text-2xl md:text-3xl">Our battery range</h2>
            <p className="text-gray-500 text-sm mt-2">All prices are fully installed including VAT.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {models.map((m, i) => (
              <div
                key={m.brand}
                style={{ transitionDelay: `${i * 100}ms` }}
                className={`bg-white rounded-2xl border-2 p-6 relative transition-all duration-300 hover:-translate-y-1 ${'popular' in m && m.popular ? 'border-[#4CAF50]' : 'border-gray-100'} ${modelsAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              >
                {'popular' in m && m.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#4CAF50] text-white text-[10px] font-bold px-4 py-1 rounded-full uppercase tracking-wide whitespace-nowrap">
                    Most Popular
                  </div>
                )}
                <div className="text-[#145A32] font-black text-lg mb-1">{m.brand}</div>
                <div className="text-gray-400 text-xs mb-3">{m.capacity} · {m.cycles} cycles · {m.warranty}</div>
                <div className="text-[#1F2933] font-bold text-2xl mb-2">{m.price}</div>
                <p className="text-gray-500 text-sm mb-5 leading-relaxed">{m.desc}</p>
                <a href="#get-quote" className="block w-full text-center bg-gradient-to-r from-[#145A32] to-[#4CAF50] text-white font-bold py-3 rounded-xl text-sm hover:opacity-90 transition-all cursor-pointer whitespace-nowrap">
                  Get this battery
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Strip */}
      <CoverageStrip />

      {/* FAQs */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div
            ref={faqAnim.ref}
            className={`text-center mb-10 transition-all duration-700 ${faqAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <div className="text-xs font-bold uppercase tracking-widest text-[#4CAF50] mb-2">FAQ</div>
            <h2 className="font-heading font-black text-[#1F2933] text-2xl md:text-3xl">Frequently asked questions</h2>
          </div>
          <div className="flex flex-col gap-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                style={{ transitionDelay: `${i * 80}ms` }}
                className={`border border-gray-100 rounded-xl overflow-hidden transition-all duration-500 ${faqAnim.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              >
                <button
                  className="w-full flex items-center justify-between p-5 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-semibold text-[#1F2933] text-sm pr-4">{faq.q}</span>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className={`ri-${openFaq === i ? 'subtract' : 'add'}-line text-[#4CAF50] text-lg`} />
                  </div>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === i ? 'max-h-40' : 'max-h-0'}`}>
                  <p className="px-5 pb-5 text-gray-500 text-sm leading-relaxed">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote form */}
      <section id="get-quote" className="py-14 md:py-20 bg-[#145A32]">
        <div className="max-w-2xl mx-auto px-4 md:px-8 text-center">
          <div className="text-[#7AC943] text-xs font-bold uppercase tracking-widest mb-3">GET STARTED</div>
          <h2 className="font-heading font-black text-white text-2xl md:text-3xl mb-3">Get your free battery quote today</h2>
          <p className="text-white/70 text-sm mb-8 leading-relaxed">Enter your postcode and we&apos;ll match you with a certified battery installer in your area.</p>
          <div className="bg-white rounded-2xl p-6 md:p-8">
            <BatteryQuoteForm />
          </div>
        </div>
      </section>

      <Footer />
      <SiteWidgets />
    </main>
  );
}
